/**
 * Firestore nested-array codec for generation-impact scenario snapshots.
 *
 * Firestore rejects an array stored directly inside another array
 * ("INVALID_ARGUMENT: Property result contains an invalid nested entity" —
 * live, William, Redhead 2026-08-25). The scenario RESULT legitimately
 * carries such shapes: `noiseField.bands[].rings` and exclusion-zone
 * `polygons` are `Array<Array<{lat,lng}>>`, and `resultHash` byte-stability
 * is a replay lock, so the SHAPE cannot change. The codec therefore lives at
 * the persistence boundary only: every inner array is wrapped as
 * `{ __nestedArray: [...] }` on write and unwrapped on read, so hashes are
 * always computed over the ORIGINAL shape and round-trip is identity.
 *
 * Generic by design — any future nested-array field in input or result is
 * covered without another incident.
 *
 * The encoding covers exactly THREE Firestore destinations — the scenario
 * document, the site-doc `promotedGenerationScenarioSnapshot` embed, and the
 * immutable layout publication's `scenarioSnapshot`. It NEVER covers the SED,
 * which is GCS JSON: nested arrays are legal there and the body is inside
 * `sedContentHash` / `siteEvidence.canonicalText`, so encoding it would drift
 * every content hash. This module lives under `lib/` (not `server/`) because
 * the client reads the site-doc embed and must decode it too.
 */

import type { GenerationImpactScenarioSnapshot } from '../../types/generationImpact';

/** The single wrapper key. Exported so detectors cannot drift from the codec. */
export const FIRESTORE_NESTED_ARRAY_KEY = '__nestedArray';

/**
 * Only literal maps are traversed. A class instance (Firestore `Timestamp`,
 * `GeoPoint`, `Date`, `Buffer`, a `FieldValue` sentinel) passes through BY
 * REFERENCE — the codec must never re-prototype a persistence value into a
 * plain object, which would silently empty a `Date` and de-Buffer a blob. The
 * `null`-prototype arm keeps `Object.create(null)` maps traversable.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value) as object | null;
  return proto === Object.prototype || proto === null;
}

/** An encoded inner array: a single-key map whose only key holds an array. */
function isNestedArrayWrapper(value: unknown): value is Record<string, unknown[]> {
  return isPlainObject(value)
    && Object.keys(value).length === 1
    && FIRESTORE_NESTED_ARRAY_KEY in value
    && Array.isArray(value[FIRESTORE_NESTED_ARRAY_KEY]);
}

export function encodeFirestoreNestedArrays<T>(value: T): T {
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((entry) => {
      if (Array.isArray(entry)) {
        changed = true;
        return { [FIRESTORE_NESTED_ARRAY_KEY]: encodeFirestoreNestedArrays(entry) };
      }
      const next = encodeFirestoreNestedArrays(entry);
      if (next !== entry) changed = true;
      return next;
    });
    // Structural no-op values are returned by reference so a scenario without
    // nested arrays is byte- AND identity-identical to its input.
    return (changed ? out : value) as unknown as T;
  }
  if (isPlainObject(value)) {
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      const next = encodeFirestoreNestedArrays(entry);
      if (next !== entry) changed = true;
      out[key] = next;
    }
    return (changed ? out : value) as T;
  }
  return value;
}

export function decodeFirestoreNestedArrays<T>(value: T): T {
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((entry) => {
      if (isNestedArrayWrapper(entry)) {
        changed = true;
        return decodeFirestoreNestedArrays(entry[FIRESTORE_NESTED_ARRAY_KEY]);
      }
      const next = decodeFirestoreNestedArrays(entry);
      if (next !== entry) changed = true;
      return next;
    });
    return (changed ? out : value) as unknown as T;
  }
  if (isPlainObject(value)) {
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      const next = decodeFirestoreNestedArrays(entry);
      if (next !== entry) changed = true;
      out[key] = next;
    }
    return (changed ? out : value) as T;
  }
  return value;
}

/**
 * Decode the site document's promoted-scenario embed and nothing else.
 *
 * Every raw `siteSnap.data() as SiteEntity` read that hands the BODY to a
 * consumer must pass through here. Only that one field is decoded: the rest of
 * the site document must keep its persisted shape and identity. Decode returns
 * its input BY REFERENCE when nothing is wrapped, so a site without the embed
 * — and every site written before the encoding existed — is returned unchanged
 * and allocates nothing.
 */
export function decodeSiteDocumentGenerationEmbed<
  T extends { promotedGenerationScenarioSnapshot?: GenerationImpactScenarioSnapshot },
>(site: T): T {
  const promoted = site.promotedGenerationScenarioSnapshot;
  if (!promoted) return site;
  const decoded = decodeFirestoreNestedArrays(promoted);
  if (decoded === promoted) return site;
  // Spread of a generic widens to `T & {…}`; only the one field changes.
  return { ...site, promotedGenerationScenarioSnapshot: decoded } as T;
}

/**
 * True when `value` still carries the persistence encoding. The wrapper can
 * only ever occur as an ARRAY ELEMENT, so this mirrors decode exactly: a map
 * that merely happens to be keyed `__nestedArray` outside an array is not an
 * encoding.
 *
 * DEEP: cost is the whole body. Never call it on a production hot path — go
 * through `firestoreNestedArrayDecodeDefect`, which is inert outside developer
 * builds. Calling it directly is only safe on a branch that has already
 * failed.
 */
export function hasFirestoreNestedArrayWrapper(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some((entry) => isNestedArrayWrapper(entry) || hasFirestoreNestedArrayWrapper(entry));
  }
  if (isPlainObject(value)) {
    for (const entry of Object.values(value)) {
      if (hasFirestoreNestedArrayWrapper(entry)) return true;
    }
  }
  return false;
}

/**
 * Developer instrumentation for the decode boundary — NEVER a production
 * control-flow path.
 *
 * A missed decode is a read-boundary bug, not a runtime condition: it cannot
 * arise from user input, and the deployed behaviour of an un-decoded body must
 * degrade to correct data via the reader's decode, never to a 5xx. So this is
 * DEFAULT-DENY — an unset or unrecognised environment is `off`, which keeps the
 * deep scan off every hot path — and production is refused BEFORE any opt-in is
 * read, so no environment variable can arm a deployed process.
 *
 * Where each mode is reachable, checked against the actual scripts:
 *   - `throw` under a test runner (`VITEST`, or `NODE_ENV=test`), so a new
 *     un-decoded crossing is a red test.
 *   - `warn` under `pnpm dev` (= `tsx server.ts`, which sets no `NODE_ENV`).
 *     Its dev branch calls Vite's `createServer`, and Vite's `resolveConfig`
 *     assigns `process.env.NODE_ENV = 'development'` whenever it is unset —
 *     before `app.listen`, so every request is served in `warn`. That is a
 *     side effect of a third-party call, so it is a bonus, not the contract:
 *     the opt-in below is what this module actually promises.
 *   - `warn` / `throw` on demand via `DEV_NESTED_ARRAY_DIAGNOSTICS`, the one
 *     signal that depends on no harness. It is what makes the diagnostic
 *     reachable in the developer workflows Vite never touches — a hand-run
 *     repro (`DEV_NESTED_ARRAY_DIAGNOSTICS=throw tsx scripts/repro.ts`) and a
 *     locally built `node dist/server.cjs`, both of which leave `NODE_ENV`
 *     unset.
 *   - `off` everywhere else. Note that only the exact string `development`
 *     warns: any other non-production `NODE_ENV` (`staging`, `ci`, …) is `off`
 *     and needs the opt-in. A browser bundle has no `process` at all and is
 *     therefore always `off`; the client's decode point is
 *     `normalizeSiteEntity`, and its guard is the source gate, not this.
 */
type NestedArrayDiagnosticsMode = 'off' | 'warn' | 'throw';

/**
 * The hand-armed developer signal. Exported so the reachability test cannot
 * drift from the name a developer is told to set.
 */
export const NESTED_ARRAY_DIAGNOSTICS_ENV = 'DEV_NESTED_ARRAY_DIAGNOSTICS';

function nestedArrayDiagnosticsMode(): NestedArrayDiagnosticsMode {
  if (typeof process === 'undefined') return 'off';
  const env = process.env;
  if (!env) return 'off';
  if (env.VITEST || env.NODE_ENV === 'test') return 'throw';
  // Production is denied before the opt-in is read, so a stray environment
  // variable can never put the deep scan on a live route.
  if (env.NODE_ENV === 'production') return 'off';
  const requested = env[NESTED_ARRAY_DIAGNOSTICS_ENV];
  if (requested === 'off' || requested === 'warn' || requested === 'throw') return requested;
  return env.NODE_ENV === 'development' ? 'warn' : 'off';
}

/**
 * Report a body that reached a decode-dependent consumer still encoded.
 *
 * Returns a message only in `throw` mode — a test runner, or an explicit
 * `DEV_NESTED_ARRAY_DIAGNOSTICS=throw` — so the caller can raise it in whatever
 * error type its own contract uses and a new un-decoded crossing is a red test.
 * `warn` logs and returns null; `off` returns null without ever touching the
 * body.
 */
export function firestoreNestedArrayDecodeDefect(
  subject: string,
  ...values: readonly unknown[]
): string | null {
  const mode = nestedArrayDiagnosticsMode();
  if (mode === 'off') return null;
  if (!values.some(hasFirestoreNestedArrayWrapper)) return null;
  const message = `${subject} is still Firestore nested-array encoded;`
    + ' decode it at the Firestore read boundary';
  if (mode === 'throw') return message;
  console.error(`[firestoreNestedArrayCodec] ${message}`);
  return null;
}

/** `firestoreNestedArrayDecodeDefect` for callers with no error type of their own. */
export function assertFirestoreNestedArraysDecoded(
  subject: string,
  ...values: readonly unknown[]
): void {
  const defect = firestoreNestedArrayDecodeDefect(subject, ...values);
  if (defect) throw new Error(defect);
}
